import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Users, Star, Clock, UserPlus, ArrowRight, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { useAuth } from '../../context/AuthContext';
import { functions, db } from '../../lib/firebase';

// ---------- Types ----------
interface LoyaltyClient {
  id: string;
  displayName: string;
  email: string;
  status: string;
  pointsBalance: number;
  lifetimeEarned: number;
  totalRedeemed: number;
  loanCount: number;
  lastLoginAt: string | null;
  lastAccrualMonth: string | null;
  createdAt: string;
}

interface GetLoyaltyClientsResponse {
  clients: LoyaltyClient[];
  hasMore: boolean;
}

interface DashboardStats {
  totalClients: number;
  totalPointsIssued: number;
  pendingRedemptions: number;
  activeClients: number;
}

interface RedemptionRow {
  id: string;
  clientName: string;
  giftCard: string;
  points: number;
  value: number;
  status: 'pending' | 'approved' | 'fulfilled' | 'rejected';
  date: string;
}

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
  approved: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
  fulfilled: 'bg-green-500/20 text-green-400 border border-green-500/30',
  rejected: 'bg-red-500/20 text-red-400 border border-red-500/30',
};

const LoyaltyDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { companyId } = useAuth();

  const [stats, setStats] = useState<DashboardStats>({
    totalClients: 0,
    totalPointsIssued: 0,
    pendingRedemptions: 0,
    activeClients: 0,
  });
  const [recentRedemptions, setRecentRedemptions] = useState<RedemptionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch clients via Cloud Function
      const getLoyaltyClientsFn = httpsCallable<
        { companyId: string; status?: string; limit?: number },
        GetLoyaltyClientsResponse
      >(functions, 'getLoyaltyClients');

      const clientsResult = await getLoyaltyClientsFn({
        companyId,
        status: 'all',
        limit: 200,
      });

      const clients = clientsResult.data.clients;

      // Derive stats from client data
      const totalClients = clients.length;
      const totalPointsIssued = clients.reduce((sum, c) => sum + (c.lifetimeEarned || 0), 0);
      const activeClients = clients.filter((c) => c.status === 'active').length;

      // Query Firestore for recent redemptions (last 5)
      const redemptionsQuery = query(
        collection(db, 'loyalty_redemptions'),
        where('companyId', '==', companyId),
        orderBy('createdAt', 'desc'),
        limit(5)
      );

      const redemptionsSnap = await getDocs(redemptionsQuery);

      const redemptions: RedemptionRow[] = redemptionsSnap.docs.map((doc) => {
        const data = doc.data();
        const createdAt = data.createdAt?.toDate?.() || null;
        return {
          id: doc.id,
          clientName: (data.clientDisplayName as string) || 'Unknown',
          giftCard: (data.giftCardCategory as string) || '--',
          points: (data.pointsSpent as number) || 0,
          value: ((data.pointsSpent as number) || 0) / 100,
          status: (data.status as RedemptionRow['status']) || 'pending',
          date: createdAt ? createdAt.toISOString().split('T')[0] : '--',
        };
      });

      // Count pending redemptions
      const pendingQuery = query(
        collection(db, 'loyalty_redemptions'),
        where('companyId', '==', companyId),
        where('status', '==', 'pending')
      );
      const pendingSnap = await getDocs(pendingQuery);

      setStats({
        totalClients,
        totalPointsIssued,
        pendingRedemptions: pendingSnap.size,
        activeClients,
      });
      setRecentRedemptions(redemptions);
    } catch (err: any) {
      console.error('[LoyaltyDashboardPage] Failed to fetch dashboard data:', err);
      setError(err?.message || 'Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Gift}
        title="Loyalty Rewards"
        subtitle="Manage your client loyalty program and track engagement"
        onRefresh={fetchDashboardData}
        isRefreshing={loading}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/loyalty/bulk-activate')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
            >
              Bulk Activate
            </button>
            <button
              onClick={() => navigate('/loyalty/clients')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Invite Clients
            </button>
          </div>
        }
      />

      {/* Loading State */}
      {loading && (
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-12 flex flex-col items-center justify-center gap-3">
          <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
          <p className="text-sm text-gray-400">Loading dashboard data...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-gray-800/50 border border-red-500/30 rounded-xl p-8 flex flex-col items-center justify-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-400" />
          <p className="text-sm text-red-300">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors mt-2"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      )}

      {/* Dashboard content */}
      {!loading && !error && (
        <>
          {/* Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Clients"
              value={stats.totalClients.toLocaleString()}
              icon={<Users className="w-6 h-6" />}
              gradientColor="#8b5cf6"
              periodLabel="All time"
            />
            <StatCard
              title="Points Issued"
              value={stats.totalPointsIssued.toLocaleString()}
              icon={<Star className="w-6 h-6" />}
              gradientColor="#f59e0b"
              periodLabel="All time"
            />
            <StatCard
              title="Pending Redemptions"
              value={stats.pendingRedemptions.toLocaleString()}
              icon={<Clock className="w-6 h-6" />}
              gradientColor="#ef4444"
              periodLabel="Awaiting action"
            />
            <StatCard
              title="Active Clients"
              value={stats.activeClients.toLocaleString()}
              icon={<Gift className="w-6 h-6" />}
              gradientColor="#10b981"
              periodLabel="Enrolled"
            />
          </div>

          {/* Recent Redemption Requests */}
          <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700/50">
              <h3 className="text-lg font-semibold text-white">Recent Redemption Requests</h3>
              <button
                onClick={() => navigate('/loyalty/redemptions')}
                className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                View All <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {recentRedemptions.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <p>No redemption requests yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-400">
                  <thead className="text-xs text-gray-400 uppercase bg-gray-700/50">
                    <tr>
                      <th scope="col" className="px-6 py-3">Client</th>
                      <th scope="col" className="px-6 py-3">Gift Card</th>
                      <th scope="col" className="px-6 py-3">Points</th>
                      <th scope="col" className="px-6 py-3">Value</th>
                      <th scope="col" className="px-6 py-3">Status</th>
                      <th scope="col" className="px-6 py-3">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRedemptions.map((req) => (
                      <tr key={req.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                        <td className="px-6 py-4 font-medium text-white">{req.clientName}</td>
                        <td className="px-6 py-4">{req.giftCard}</td>
                        <td className="px-6 py-4">{req.points.toLocaleString()}</td>
                        <td className="px-6 py-4">${req.value.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-block px-2.5 py-0.5 text-xs font-semibold uppercase rounded-full ${STATUS_STYLES[req.status]}`}>
                            {req.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {req.date !== '--'
                            ? new Date(req.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
                            : '--'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/loyalty/clients')}
              className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-5 text-left hover:bg-gray-700/30 hover:border-gray-600/50 transition-all group"
            >
              <Users className="w-6 h-6 text-purple-400 mb-3" />
              <h4 className="text-white font-medium mb-1">Manage Clients</h4>
              <p className="text-sm text-gray-400">View and invite clients to your loyalty program</p>
              <span className="inline-flex items-center gap-1 mt-3 text-sm text-blue-400 group-hover:text-blue-300">
                Go to Clients <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </button>

            <button
              onClick={() => navigate('/loyalty/redemptions')}
              className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-5 text-left hover:bg-gray-700/30 hover:border-gray-600/50 transition-all group"
            >
              <Gift className="w-6 h-6 text-amber-400 mb-3" />
              <h4 className="text-white font-medium mb-1">Redemptions</h4>
              <p className="text-sm text-gray-400">Review and process gift card redemption requests</p>
              <span className="inline-flex items-center gap-1 mt-3 text-sm text-blue-400 group-hover:text-blue-300">
                Go to Redemptions <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </button>

            <button
              onClick={() => navigate('/loyalty/settings')}
              className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-5 text-left hover:bg-gray-700/30 hover:border-gray-600/50 transition-all group"
            >
              <Star className="w-6 h-6 text-emerald-400 mb-3" />
              <h4 className="text-white font-medium mb-1">Program Settings</h4>
              <p className="text-sm text-gray-400">Configure points rates, branding, and redemption options</p>
              <span className="inline-flex items-center gap-1 mt-3 text-sm text-blue-400 group-hover:text-blue-300">
                Go to Settings <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default LoyaltyDashboardPage;
